import { FunctionComponent, useEffect } from "react";
import RivalsButton from "rivals/components/shared/Button";
import Select from "rivals/components/shared/Form/Select";
import { getSites as getSitesAction } from "rivals/redux/rails/actions";
import { getSites } from "rivals/redux/rails/selectors";
import { getSubmitting } from "rivals/redux/subscriptionAddons/selectors";
import { selectHasAdLite } from "rivals/redux/user/selectors";
import { isAddOnSubscribed } from "rivals/shared/utils/addons";
import { useAppDispatch, useAppSelector } from "rivals/shared/utils/hooks";
import { setData } from "rivals/shared/utils/qa";
import { buildForumUrl } from "rivals/shared/utils/routes";
import styles from "./AddonCard.module.scss";
import strings from "./strings";
import { OwnProps } from "./types";
import {
  ADDON_MODAL_CONSTANTS,
  LEARN_MORE_ADDON_MODAL_STEP,
  PURCHASE_ADDON_MODAL_STEP,
  UPSELL_FUNNEL_ADDON_MODAL_STEP
} from "../constants";
import addonStrings from "../strings";

const { SPY, AD_LITE, ALL_ACCESS, BUNDLE } = ADDON_MODAL_CONSTANTS;
const { purchasedText } = addonStrings;

const {
  addText,
  chooseYourOwnTextPrompt,
  chooseYourOwn,
  learnMoreText,
  purchasedGo
} = strings;

const AddonCard: FunctionComponent<OwnProps> = ({
  addOnCardData,
  addOns,
  chooseYourOwnAddOn,
  chooseYourOwnAddOnList = null,
  chooseYourOwnSchool = false,
  handleDropdownChange,
  openModal
}) => {
  const dispatch = useAppDispatch();
  const alreadySubscribed = isAddOnSubscribed(addOnCardData);
  const dataPhilter = "addon-card";
  const sites = useAppSelector(getSites) || [];
  const allAccess =
    addOnCardData.name == "All-Access" && chooseYourOwnSchool !== true;
  const hasAdLite = useAppSelector(selectHasAdLite);
  const showAdLiteAccess =
    addOnCardData.addOnGroup?.staticGroupType === AD_LITE && hasAdLite;

  const associatedSite = sites.find(site => site.id === addOnCardData.siteId);
  const submitting = useAppSelector(getSubmitting);
  const showAddButton =
    !chooseYourOwnSchool || (chooseYourOwnSchool && chooseYourOwnAddOn);
  const allAccessAddOn =
    addOns && addOns.length && addOns.find(addOn => addOn.type === ALL_ACCESS);
  const bundleAddOn =
    addOns && addOns.length && addOns.find(addOn => addOn.type === BUNDLE);

  const logoImg = (
    <div
      className={styles.siteLabel}
      style={{
        backgroundColor: addOnCardData.siteColor || styles["rivals-blue"]
      }}
      {...setData(dataPhilter, "site-logo")}
    >
      <img
        alt="site logo"
        className={styles.siteLogo}
        src={addOnCardData.siteLogoUrl || ""}
      />
    </div>
  );

  useEffect(() => {
    if (!sites.length) {
      dispatch(getSitesAction());
    }
  }, []);

  const handleAddClick = (): void => {
    if (addOnCardData.type === SPY) {
      if (
        allAccessAddOn ||
        (bundleAddOn &&
          bundleAddOn.associatedSubscription &&
          !bundleAddOn.associatedSubscription.subscribed)
      ) {
        openModal(UPSELL_FUNNEL_ADDON_MODAL_STEP, addOnCardData);
      } else {
        openModal(PURCHASE_ADDON_MODAL_STEP, addOnCardData);
      }
    } else {
      openModal(PURCHASE_ADDON_MODAL_STEP, addOnCardData);
    }
  };

  const interestsFormatted = chooseYourOwnAddOnList
    ? chooseYourOwnAddOnList.map(addOn => ({
        name: addOn.siteName || addOn.name,
        value: addOn.id
      }))
    : [];

  const chooseYourOwnCardBody = chooseYourOwnAddOnList && (
    <>
      <div
        className={styles.addonTitle}
        {...setData(dataPhilter, "choose-your-own-title")}
      >
        {chooseYourOwn}
      </div>

      {addOnCardData.duration && (
        <div
          className={styles.dateRange}
          {...setData(dataPhilter, "choose-your-own-duration")}
        >
          {addOnCardData.duration}
        </div>
      )}
      {showAddButton && (
        <div
          className={styles.price}
          {...setData(dataPhilter, "choose-your-own-price")}
        >
          {addOnCardData.formattedPriceString}
        </div>
      )}
      <div
        className={styles.summary}
        {...setData(dataPhilter, "choose-your-own-details")}
      >
        {chooseYourOwnTextPrompt}
      </div>
      {handleDropdownChange && (
        <div className={styles.dropdown}>
          <Select<number>
            disabled={false}
            onChange={handleDropdownChange}
            options={interestsFormatted}
            placeholder={"Choose Site"}
            value={chooseYourOwnAddOn}
            {...setData(dataPhilter, "choose-your-own-dropdown")}
          />
        </div>
      )}
    </>
  );

  // TODO: stop formatting these strings on the client
  // https://griosf.atlassian.net/browse/RVLS-7492
  const standardCardBody = (
    <>
      <div className={styles.addonTitle} {...setData(dataPhilter, "name")}>
        {addOnCardData.title}
      </div>
      {addOnCardData.duration && (
        <div className={styles.dateRange} {...setData(dataPhilter, "duration")}>
          {addOnCardData.duration}
        </div>
      )}
      <div
        className={styles[`price${allAccess ? "AllAccess" : ""}`]}
        {...setData(dataPhilter, "price")}
      >
        {allAccess && "Only "}
        {addOnCardData.formattedPriceString}
      </div>
      <div
        className={styles.summary}
        dangerouslySetInnerHTML={{ __html: addOnCardData.summary }}
        {...setData(dataPhilter, "details")}
      />
    </>
  );

  const addButton = (
    <RivalsButton
      buttonText={addText}
      className={styles.addButton}
      disabled={submitting}
      inverted
      size="small"
      {...setData(dataPhilter, "add")}
      onClick={(): void => handleAddClick()}
    />
  );

  const purchaseLink = associatedSite
    ? buildForumUrl(associatedSite.subdomain)
    : "";
  const isNavButton = !!purchaseLink;
  const purchasedButtonPlacer = (
    <RivalsButton
      buttonText={isNavButton ? purchasedGo : purchasedText}
      className={styles.purchasedButton}
      disabled={!isNavButton}
      size="small"
      url={purchaseLink}
      {...setData(dataPhilter, isNavButton ? "purchasedGo" : "purchased")}
    />
  );
  let topLeftCallToAction: JSX.Element = addButton;
  if (alreadySubscribed || showAdLiteAccess) {
    topLeftCallToAction = purchasedButtonPlacer;
  }

  const header = (
    <div
      className={
        (!chooseYourOwnSchool &&
          addOnCardData.siteLogoUrl &&
          styles.addOnCardHeader) ||
        styles.addOnCardHeaderButtonOnly
      }
    >
      {!chooseYourOwnSchool && addOnCardData.siteLogoUrl && logoImg}
      {showAddButton && topLeftCallToAction}
    </div>
  );

  const learnMoreButton = (
    <RivalsButton
      backgroundColor="transparent"
      buttonText={learnMoreText}
      className={styles.learnMoreButton}
      disabled={submitting || !showAddButton}
      inverted
      onClick={(): void => {
        openModal(LEARN_MORE_ADDON_MODAL_STEP, addOnCardData);
      }}
      size="small"
      {...setData(dataPhilter, "learn-more")}
    />
  );

  return (
    <>
      <div
        className={`${styles.addonCard} ${allAccess && ` ${styles.fancyCard}`}`}
        {...setData(dataPhilter, "add-on-card")}
      >
        {header}
        {chooseYourOwnSchool ? chooseYourOwnCardBody : standardCardBody}
        {learnMoreButton}
      </div>
    </>
  );
};

export default AddonCard;
